import { useEffect } from 'react';
import { RecommendationSource } from '@osmosis/shared/types/recommended';
import { useUserContext } from '@osmosis/nextjs-components/context/UserContext';
import {
  ParsedQuery,
  usePagesContext,
} from '@osmosis/nextjs-components/context';
import { setCookie, getCookie } from 'cookies-next';
import { VISITED_PAGE_COOKIE } from '@osmosis/shared/constants/cookies';

const isLearnRoute = (pathname: string | null) => {
  return (
    pathname?.startsWith('/learn') ||
    pathname?.startsWith('/video') ||
    pathname?.startsWith('/learn-quiz')
  );
};
const isAnswerPage = (pathname: string | null) => {
  return pathname?.startsWith('/answers');
};

const isTrackedPage = (pathname: string | null) =>
  isLearnRoute(pathname) || isAnswerPage(pathname);

const getRecSource = (
  pathname: string | null,
  params: Record<string, string | string[]>,
  query: ParsedQuery,
): RecommendationSource => {
  const concept = Array.isArray(params?.concept)
    ? params?.concept[0]
    : params?.concept;
  const rec: RecommendationSource = {
    type: '',
    query: concept || (params?.learnPage as string) || '',
    fromPath: query?.from as string,
    source: 'visited',
  };
  if (isLearnRoute(pathname)) rec.type = 'learn';
  if (isAnswerPage(pathname)) rec.type = 'answers';
  return rec;
};

/**
 * Hook to set a cookie when a user visits a page.
 */
const useVisitedPage = () => {
  const { user, isUserLoaded } = useUserContext();
  const { pathname, params, query } = usePagesContext();

  useEffect(() => {
    if (!isTrackedPage(pathname) || !isUserLoaded || user) {
      return;
    }

    const recSource = getRecSource(pathname, params, query);
    const existingRecSource = getCookie(VISITED_PAGE_COOKIE);
    if (existingRecSource) {
      const parsedRecSource = JSON.parse(
        existingRecSource as string,
      ) as RecommendationSource;

      if (
        parsedRecSource.type === recSource.type &&
        parsedRecSource.query === recSource.query
      ) {
        return;
      }
    }
    if (recSource.type && recSource.query) {
      setCookie(VISITED_PAGE_COOKIE, JSON.stringify(recSource));
    }
  }, [user, isUserLoaded, pathname, params, query]);
};

export default useVisitedPage;
